// BattleStage.jsx
// Bloom & Blight — the console-battle presentation, for both solo (vs the
// Sprout AI) and online guest matches (vs a friend, via join code). Online
// state is server-authoritative: moves go through callables and arrive back
// by snapshot; the view is perspective-swapped so YOU are always player 0,
// letting one render path serve both modes. Rules live in gameEngine.js.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BIOMES,
  BIOME_IDS,
  CARDS,
  COPIES_FOR_SPECIES_COUNT,
  PETALS_TO_WIN,
  SPECIES,
  SPECIES_IDS,
  WEATHER,
} from "../gameData";
import {
  compostCard,
  createDemoGame,
  digUpPlant,
  endTurn,
  getTargets,
  isGrown,
  newGame,
  plantCard,
  playBlight,
  playTreatment,
  rollBiome,
  waterPlant,
} from "../gameEngine";
import { chooseAiAction } from "../aiOpponent";
import LeaflingSprite from "../LeaflingSprite";
import { PetalFlower } from "../PlantArt";
import BiomeBackdrop from "./BiomeBackdrop";
import * as online from "./onlineMatch";
import { openInNewTab } from "../../sharedFunctions";
import { APP_STORE_URL } from "../../constants";
import "./battle.css";

const HUMAN = 0;
const AI = 1;
const AI_STEP_CAP = 14;
// Sprout pacing per speed setting: step = delay before each action; reveal =
// how long a card play is shown front-and-center (0 = wait for a manual click).
const SPEED = {
  manual: { step: 650, reveal: 0 },
  slow: { step: 3200, reveal: 4600 },
  normal: { step: 1800, reveal: 2800 },
  fast: { step: 320, reveal: 520 },
};
// Touch devices fire a synthetic mouseenter on tap that fights the click
// toggle (opens then immediately closes → needs a second tap). Only wire
// hover-to-preview on real pointer devices.
const CAN_HOVER =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/* ---------------------------------------------------------- odds dots */
// Visualize a biome's forecast die as six pips. `OddsDie` colors the whole
// die by weather (●●●○○○); `OddsRow` shows one weather's share as filled dots.

function OddsDie({ odds }) {
  const pips = [
    ...Array(odds.sunny).fill("sunny"),
    ...Array(odds.cloudy).fill("cloudy"),
    ...Array(odds.rain).fill("rain"),
  ];
  return (
    <span className="bs-odds-die" aria-label={`${odds.sunny}/6 sunny, ${odds.cloudy}/6 cloudy, ${odds.rain}/6 rain`}>
      {pips.map((w, i) => <i key={i} className={`w-${w}`} />)}
    </span>
  );
}

function OddsRow({ weatherId, count }) {
  return (
    <span className="bs-odds-row">
      {Array.from({ length: 6 }, (_, i) => (
        <i key={i} className={i < count ? `w-${weatherId}` : "empty"} />
      ))}
    </span>
  );
}

/* ---------------------------------------------------------- gauge plate */

function GaugePlate({ plant, compact, onInspect }) {
  const sp = SPECIES[plant.speciesId];
  const lightPct = Math.min(1, plant.light / sp.lightNeed) * 100;
  const cells = 8; // uniform water track for every species; the band marks each one's sweet spot
  const inSpot = plant.water >= sp.waterMin && plant.water <= sp.waterMax;
  return (
    <div
      className={`bs-plate ${compact ? "compact" : ""} ${onInspect ? "tappable" : ""}`}
      onClick={onInspect}
      role={onInspect ? "button" : undefined}
      tabIndex={onInspect ? 0 : undefined}
      onKeyDown={onInspect ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onInspect(); } } : undefined}
    >
      <div className="bs-plate-name">
        <strong>{sp.name}</strong>
        <span className="bs-plate-status">
          {plant.growLamp && <i title="Grow Lamp: +1 ☀️ every forecast">🔦</i>}
          {plant.humidifier && <i title="Humidifier: weather can't change 💧">🫧</i>}
          {plant.umbrella && <i className="bad" title="Umbrella blight: cut off from weather">☂️</i>}
        </span>
      </div>
      <div className="bs-bar" title={`Light ${plant.light} / 🎯 ${sp.lightNeed}+`}>
        <span className="bs-bar-ico">☀️</span>
        <span className="bs-bar-track">
          <span className={`bs-bar-fill sun ${plant.light >= sp.lightNeed ? "full" : ""}`} style={{ width: `${lightPct}%` }} />
        </span>
        <span className="bs-bar-num">{plant.light}<em>/{sp.lightNeed}</em></span>
      </div>
      <div className="bs-bar" title={`Water ${plant.water} / 🎯 ${sp.waterMin}–${sp.waterMax}`}>
        <span className="bs-bar-ico">💧</span>
        <span className="bs-bar-cells">
          {Array.from({ length: cells }, (_, i) => {
            const lvl = i + 1;
            const band = lvl >= sp.waterMin && lvl <= sp.waterMax;
            const fill = lvl <= plant.water;
            return <span key={i} className={`bs-cell ${band ? "band" : ""} ${fill ? (band ? "ok" : "over") : ""}`} />;
          })}
        </span>
        <span className={`bs-bar-num ${inSpot ? "good" : ""}`}>{plant.water}{plant.water > cells ? "!" : ""}{inSpot ? " ✓" : ""}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ actors */

function PlantActor({
  plant,
  slotClass,
  enemy,
  isTarget,
  onClick,
  badge,
  onInspect,
  greenhouseOp,
  onGreenhouseChange,
  greenhouseFull,
}) {
  if (!plant) {
    return (
      <div className={`bs-actor empty ${slotClass} ${isTarget ? "targetable" : ""}`}>
        {isTarget && <span className="bs-marker">▼</span>}
        <button className="bs-mound" onClick={onClick} disabled={!isTarget} aria-label={isTarget ? "Plant here" : "Empty spot"}>
          <span className="bs-platform" />
          <img src="/game-assets/pot-yellow.png" alt="" className="bs-ghost-pot" />
          {isTarget && <small>plant here</small>}
        </button>
      </div>
    );
  }
  const sp = SPECIES[plant.speciesId];
  // Mid-stage: seedlings start small and grow toward (but never reach) full
  // size — the bloom's grown sprite at ~1.15 is the visible graduation.
  const growth = 0.68 + 0.24 * Math.min(1, plant.light / sp.lightNeed);
  return (
    <div className={`bs-actor ${slotClass} ${enemy ? "enemy" : ""} ${isTarget ? "targetable" : ""}`}>
      {onGreenhouseChange && (
        <div className="bs-greenhouse-controls" aria-label={`Adjust ${sp.name}`}>
          {[["light", 1, "+☀️"], ["water", -1, "−💧"], ["water", 1, "+💧"]].map(([stat, delta, label]) => (
            <button
              key={label}
              className={greenhouseOp?.stat === stat && greenhouseOp?.delta === delta ? "on" : ""}
              disabled={greenhouseFull && !greenhouseOp}
              onClick={() => onGreenhouseChange(stat, delta)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <GaugePlate plant={plant} compact={enemy} onInspect={onInspect} />
      {isTarget && <span className="bs-marker">▼</span>}
      <button
        className="bs-actor-hit"
        style={{ "--growth": growth }}
        onClick={onClick}
        disabled={!isTarget}
        aria-label={`${sp.name}${isTarget ? ", valid target" : ""}`}
      >
        <span className="bs-platform" />
        <span className="bs-actor-sprite">
          <LeaflingSprite speciesId={plant.speciesId} owner={enemy ? "sprout" : "you"} plant={plant} stage="mid" />
        </span>
        <span className="bs-ring" />
        <span className="bs-ground-shadow" />
      </button>
      {badge && <span className="bs-badge">{badge}</span>}
    </div>
  );
}

/* ------------------------------------------------------------- tray */

function TrayCard({ card, selected, disabled, unplayable, onClick, onHover, stats }) {
  const def = CARDS[card.id];
  const sp = stats && def.type === "plant" ? SPECIES[card.id] : null;
  return (
    <button
      className={`bs-tray-card type-${def.type} ${selected ? "selected" : ""} ${unplayable ? "unplayable" : ""} ${stats ? "with-stats" : ""}`}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => onHover && onHover(card.uid)}
      onMouseLeave={() => onHover && onHover(null)}
      onFocus={() => onHover && onHover(card.uid)}
      onBlur={() => onHover && onHover(null)}
    >
      <span className="bs-tray-art">
        {def.type === "plant" ? <LeaflingSprite speciesId={card.id} /> : <i>{def.icon}</i>}
      </span>
      <strong>{def.name}</strong>
      {sp && (
        <small className="bs-tray-stats">
          🎯 ☀️{sp.lightNeed} · 💧{sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`}
        </small>
      )}
    </button>
  );
}

/* ----------------------------------------------------- card info panel */

function CardInfo({ card, pinned, onCancel }) {
  const def = CARDS[card.id];
  const sp = def.type === "plant" ? SPECIES[card.id] : null;
  return (
    <aside className={`bs-inspect ${pinned ? "pinned" : ""}`}>
      <span className={`bs-inspect-type type-${def.type}`}>{def.icon} {def.type}{def.status ? " · stays on plant" : ""}</span>
      <strong>{def.name}</strong>
      <p>{def.text}</p>
      {sp && (
        <div className="bs-inspect-stats">
          <span>🎯 ☀️ {sp.lightNeed}+</span>
          <span>💧 {sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`}</span>
          <span title={`Difficulty ${sp.difficulty}/5`}>{"🌿".repeat(sp.difficulty)}</span>
        </div>
      )}
      {pinned && <button className="bs-link" onClick={onCancel}>cancel ✕</button>}
    </aside>
  );
}

/* -------------------------------------- big card modal (reveal + log) */
// Shared card display: a near-full-screen card face + effect. Used for the
// Sprout/opponent play reveal and for tapping a log entry. When interactive,
// clicking the scrim or the button dismisses; when not, it's a ghost overlay
// that the caller auto-dismisses on a timer.

function CardModal({ cardId, subtitle, owner = "sprout", interactive = true, onDismiss, dismissLabel = "Got it" }) {
  const def = CARDS[cardId];
  const sp = def.type === "plant" ? SPECIES[cardId] : null;
  if (sp) return (
    <div className={`bs-cardmodal-scrim ${interactive ? "" : "ghost"}`} onClick={interactive ? onDismiss : undefined}>
      <div className="bs-cardmodal type-plant print-plant" onClick={(e) => e.stopPropagation()}>
        {subtitle && <div className="bs-cardmodal-sub">{subtitle}</div>}
        <header>
          <span>✿</span>
          <div><small>PLANT</small><h2>{sp.name}</h2></div>
        </header>
        <div className="bs-cardmodal-art">
          <LeaflingSprite speciesId={cardId} owner={owner} />
        </div>
        <div className="bs-print-plant-copy">
          <div className="bs-print-target light"><span>☀️</span><strong>{sp.lightNeed}+</strong><small>LIGHT</small></div>
          <div className="bs-print-target water"><span>💧</span><strong>{sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`}</strong><small>WATER</small></div>
          <p className="bs-cardmodal-blurb">{sp.blurb}</p>
          <p className="bs-cardmodal-effect">{def.text}</p>
          {interactive && onDismiss && <button className="bs-cta" onClick={onDismiss}>{dismissLabel}</button>}
        </div>
      </div>
    </div>
  );
  return (
    <div className={`bs-cardmodal-scrim ${interactive ? "" : "ghost"}`} onClick={interactive ? onDismiss : undefined}>
      <div className={`bs-cardmodal type-${def.type}`} onClick={(e) => e.stopPropagation()}>
        {subtitle && <div className="bs-cardmodal-sub">{subtitle}</div>}
        <span className="bs-cardmodal-badge">{def.icon} {def.type}{def.status ? " · status" : ""}</span>
        <div className="bs-cardmodal-art">
          {def.type === "plant"
            ? <LeaflingSprite speciesId={cardId} owner={owner} />
            : <span className="bs-cardmodal-icon">{def.icon}</span>}
        </div>
        <h2>{def.name}</h2>
        {sp && (
          <div className="bs-cardmodal-req">
            🎯 ☀️ {sp.lightNeed}+ · 💧 {sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`} · {"🌿".repeat(sp.difficulty)}
          </div>
        )}
        {sp && <p className="bs-cardmodal-blurb">{sp.blurb}</p>}
        <p className="bs-cardmodal-effect">{def.text}</p>
        {interactive && onDismiss && <button className="bs-cta" onClick={onDismiss}>{dismissLabel}</button>}
      </div>
    </div>
  );
}

/* ------------------------------------ in-play plant inspector overlay */
// Tap a plant's gauge plate to see its full card, live stats, and statuses.
function PlantModal({ plant, owner, onDismiss }) {
  const def = CARDS[plant.speciesId];
  const sp = SPECIES[plant.speciesId];
  const grown = isGrown(plant);
  const statuses = [];
  if (grown) statuses.push({ icon: "🌸", label: "Grown — scoring a petal for its gardener." });
  if (plant.growLamp) statuses.push({ icon: "🔦", label: "Grow Lamp: +1 ☀️ with every forecast." });
  if (plant.humidifier) statuses.push({ icon: "🫧", label: "Humidifier: weather can't change its 💧." });
  if (plant.umbrella) statuses.push({ icon: "☂️", label: "Umbrella blight: shaded from every forecast.", bad: true });
  if (plant.water < 1) statuses.push({ icon: "🥀", label: "Bone dry — thirsty for water.", bad: true });
  else if (plant.water > sp.waterMax) statuses.push({ icon: "🌊", label: "Overwatered — drooping past its sweet spot.", bad: true });
  return (
    <div className="bs-cardmodal-scrim" onClick={onDismiss}>
      <div className="bs-cardmodal type-plant plant-inspect" onClick={(e) => e.stopPropagation()}>
        <span className="bs-cardmodal-badge">{def.icon} {owner === "sprout" ? "Sprout's Leafling" : "Your Leafling"}{grown ? " · grown ✓" : ""}</span>
        <div className="bs-cardmodal-art">
          <LeaflingSprite speciesId={plant.speciesId} owner={owner} plant={plant} stage="mid" />
        </div>
        <h2>{sp.name}</h2>
        <div className="bs-cardmodal-req">
          🎯 ☀️ {sp.lightNeed}+ · 💧 {sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`} · {"🌿".repeat(sp.difficulty)}
        </div>
        <div className="bs-modal-gauges">
          <GaugePlate plant={plant} />
        </div>
        <ul className="bs-modal-status">
          {statuses.length === 0
            ? <li className="ok"><span>✅</span> Healthy and on track.</li>
            : statuses.map((s, i) => (
                <li key={i} className={s.bad ? "bad" : ""}><span>{s.icon}</span> {s.label}</li>
              ))}
        </ul>
        <p className="bs-cardmodal-effect">{sp.blurb}</p>
        <button className="bs-cta" onClick={onDismiss}>Got it</button>
      </div>
    </div>
  );
}

// Tiny card thumbnail for log rows that involved a card.
function LogCardThumb({ cardId }) {
  const def = CARDS[cardId];
  return (
    <span className={`bs-log-card type-${def.type}`}>
      {def.type === "plant" ? <LeaflingSprite speciesId={cardId} /> : <i>{def.icon}</i>}
    </span>
  );
}

/* --------------------------------------------------- species drafting */

function SpeciesDraft({ picked, onToggle }) {
  return (
    <div className="bs-draft-row">
      {SPECIES_IDS.map((id) => {
        const sp = SPECIES[id];
        const on = picked.includes(id);
        return (
          <button key={id} className={`bs-recruit ${on ? "on" : ""}`} onClick={() => onToggle(id)} aria-pressed={on}>
            <span className="bs-recruit-check" aria-hidden="true">{on ? "✓" : ""}</span>
            <span className="bs-recruit-sprite"><LeaflingSprite speciesId={id} /></span>
            <strong>{sp.name}</strong>
            <small>🎯 ☀️{sp.lightNeed} · 💧{sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`}</small>
            <small className="bs-leaves">{"🌿".repeat(sp.difficulty)}</small>
            <em>{on ? "on your team" : "tap to recruit"}</em>
          </button>
        );
      })}
    </div>
  );
}

const useDraft = () => {
  const [picked, setPicked] = useState(["calathea", "snake", "zz"]);
  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < 4 ? [...p, id] : p));
  return [picked, toggle];
};

/* ------------------------------------------------------- title screen */

function TitleScreen({ onStart, onDemo, onFriend, onGuide, onTutorial }) {
  const navigate = useNavigate();
  const [bgIdx, setBgIdx] = useState(0);
  const [picked, toggle] = useDraft();
  useEffect(() => {
    const t = setInterval(() => setBgIdx((i) => i + 1), 5000);
    return () => clearInterval(t);
  }, []);
  const valid = picked.length >= 2;

  return (
    <div className="bs-stage bs-title">
      <BiomeBackdrop biomeId={BIOME_IDS[bgIdx % BIOME_IDS.length]} />
      <div className="bs-title-veil" />
      <div className="bs-title-panel">
        <button className="bs-link" onClick={() => navigate("/")}>← reallifeplants.com</button>
        <h1>Bloom <i>&amp;</i> Blight</h1>
        <p className="bs-title-tag">
          Grow {PETALS_TO_WIN} Leaflings before your rival. The forecast is coming for both of you.
        </p>
        <SpeciesDraft picked={picked} onToggle={toggle} />
        <p className="bs-draft-note">
          Recruit 2, 3, or 4 species — 12 plant cards split evenly
          {valid && picked.length <= 4 ? ` (${picked.length} × ${COPIES_FOR_SPECIES_COUNT[picked.length]})` : ""}, plus the
          standard kit of treatments &amp; blights. Once per turn: plant a Leafling <em>or</em> dig
          one up (back into your deck), and water once. Easy Leaflings are closers.
        </p>
        <div className="bs-title-actions">
          <button className="bs-cta" disabled={!valid} onClick={() => onStart(picked)}>▶ &nbsp;Play vs Sprout</button>
          <button className="bs-cta friend" disabled={!valid} onClick={() => onFriend(picked)}>⚔ &nbsp;Play a friend</button>
          <button className="bs-ghost tutorial" onClick={onTutorial}>✨ Learn by playing</button>
          <button className="bs-ghost" onClick={onGuide}>📖 How to play</button>
          <button className="bs-ghost" onClick={onDemo}>Demo garden</button>
        </div>
        <button className="bs-link dim" onClick={() => openInNewTab(APP_STORE_URL)}>
          The web companion to Leaflings for iOS — where plants talk back →
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------ interactive tutorial */

const TUTORIAL_PLANT = {
  uid: "tutorial-pilea",
  speciesId: "pilea",
  light: 2,
  water: 2,
  umbrella: false,
  growLamp: false,
  humidifier: false,
};
const TUTORIAL_CARDS = [
  { uid: "tutorial-pilea-card", id: "pilea" },
  { uid: "tutorial-fertilizer", id: "fertilizer" },
];
const TUTORIAL_TYPE_CARDS = [
  { uid: "tutorial-type-plant", id: "zz" },
  { uid: "tutorial-type-treatment", id: "growLamp" },
  { uid: "tutorial-type-blight", id: "pests" },
];
const TUTORIAL_PRACTICE_PHASES = new Set([
  "pickPlant", "placePlant", "chooseWater", "waterPlant", "pickTreatment",
  "applyTreatment", "endTurn", "forecast", "bloomComplete",
]);
const TUTORIAL_COPY = {
  goal: [
    `Bloom ${PETALS_TO_WIN} Leaflings before your rival`,
    `Every Leafling that reaches its light target while its water is in the sweet spot blooms and banks one permanent petal. First gardener to ${PETALS_TO_WIN} petals wins. Let's earn your first one.`,
  ],
  pickPlant: ["Plant a Leafling", "Tap the Pilea card in your hand. Plant cards have a golden border."],
  placePlant: ["Choose a garden spot", "Now tap the glowing pot to move Pilea from your hand into the garden."],
  chooseWater: ["Give Pilea a drink", "You can water once each turn. Tap the Water action to choose it."],
  waterPlant: ["Choose who gets watered", "Tap Pilea. The water gauge will climb by one into its green sweet spot."],
  pickTreatment: ["Play a treatment", "Tap Fertilizer. Treatments help your garden and don't use your plant or water action."],
  applyTreatment: ["Choose a target", "Tap Pilea to add +1 light and +1 water. Watch both gauges change."],
  endTurn: ["Plan around the forecast", "Pilea needs 4 light and 3–4 water. Cloudy adds +1 light and dries −1 water — a perfect bloom. End your turn."],
  forecast: ["The forecast resolves", "Weather hits every unprotected plant between turns. Let's see what Cloudy does…"],
  bloomComplete: ["You banked your first petal!", `That bloom is safe forever. Your score is now 1/${PETALS_TO_WIN}; keep blooming before your rival reaches ${PETALS_TO_WIN}.`],
  cardTypes: ["Meet the three card families", "Tap each card to learn its job. Their border colors stay consistent, so you can read a new hand at a glance."],
  biomes: ["Every match grows in a biome", "The biome is rolled once at the start and changes the odds of sun, cloud, and rain. Tap a biome to compare its forecast die."],
  turns: ["Every turn follows the same rhythm", "Walk through one complete round. Treatments and blights can be played any time during your action step — and you can play as many as your hand allows."],
};

function TutorialScreen({ onBack }) {
  const [phase, setPhase] = useState("goal");
  const [plant, setPlant] = useState(null);
  const [seenCardTypes, setSeenCardTypes] = useState([]);
  const [cardFocus, setCardFocus] = useState("zz");
  const [biomeFocus, setBiomeFocus] = useState("mosswood");
  const [turnBeat, setTurnBeat] = useState(0);

  useEffect(() => {
    if (phase !== "forecast") return undefined;
    const growTimer = setTimeout(() => {
      setPlant((current) => ({ ...current, light: 4, water: 3 }));
    }, 850);
    const finishTimer = setTimeout(() => setPhase("bloomComplete"), 1750);
    return () => { clearTimeout(growTimer); clearTimeout(finishTimer); };
  }, [phase]);

  const reset = () => {
    setPlant(null);
    setSeenCardTypes([]);
    setCardFocus("zz");
    setBiomeFocus("mosswood");
    setTurnBeat(0);
    setPhase("goal");
  };
  const chapter = phase === "goal" ? 0
    : TUTORIAL_PRACTICE_PHASES.has(phase) ? 1
      : phase === "cardTypes" ? 2
        : phase === "biomes" ? 3
          : 4;
  const isPractice = TUTORIAL_PRACTICE_PHASES.has(phase);
  const [title, body] = TUTORIAL_COPY[phase];
  const pileaSelected = phase === "placePlant";
  const fertilizerSelected = phase === "applyTreatment";
  const plantIsTarget = phase === "waterPlant" || phase === "applyTreatment";
  const watered = ["pickTreatment", "applyTreatment", "endTurn", "forecast", "bloomComplete"].includes(phase);
  const treated = ["endTurn", "forecast", "bloomComplete"].includes(phase);
  const focusedCard = CARDS[cardFocus];
  const focusedBiome = BIOMES[biomeFocus];
  const turnBeats = [
    { icon: "🃏", title: "Draw", text: "Start your turn by drawing one card automatically." },
    { icon: "🌱", title: "Take actions", text: "Plant or dig once, water once, and play any number of treatments or blights." },
    { icon: "⛅", title: "End + weather", text: "End your turn. The shown forecast changes every unprotected plant." },
    { icon: "🧚", title: "Rival's turn", text: "Your rival takes their actions, then another forecast resolves before play returns to you." },
  ];

  const handlePlantCard = () => {
    if (phase === "pickPlant") setPhase("placePlant");
  };
  const handleFertilizer = () => {
    if (phase === "pickTreatment") setPhase("applyTreatment");
  };
  const handleGarden = () => {
    if (phase === "placePlant") {
      setPlant(TUTORIAL_PLANT);
      setPhase("chooseWater");
    } else if (phase === "waterPlant") {
      setPlant((current) => ({ ...current, water: 3 }));
      setPhase("pickTreatment");
    } else if (phase === "applyTreatment") {
      setPlant((current) => ({ ...current, light: 3, water: 4 }));
      setPhase("endTurn");
    }
  };
  const inspectType = (card) => {
    const type = CARDS[card.id].type;
    setCardFocus(card.id);
    setSeenCardTypes((seen) => seen.includes(type) ? seen : [...seen, type]);
  };

  return (
    <div className={`bs-stage bs-tutorial ${phase === "forecast" ? "bs-weather weather-cloudy" : ""}`}>
      <BiomeBackdrop biomeId="mosswood" />
      <div className="bs-title-veil" />
      <div className="bs-fx bs-fx-cloud"><i /><i /></div>

      <header className="bs-tutorial-top">
        <button className="bs-tutorial-back" onClick={onBack}>← Exit tutorial</button>
        <span className="bs-tutorial-label">Sprout School · first match</span>
        <div className="bs-tutorial-progress" aria-label={`Tutorial chapter ${chapter + 1} of 5`}>
          {["Goal", "Grow", "Cards", "Biomes", "Turns"].map((label, i) => (
            <span key={label} className={`${i < chapter ? "done" : ""} ${i === chapter ? "current" : ""}`}>
              <i>{i < chapter ? "✓" : i + 1}</i>{label}
            </span>
          ))}
        </div>
      </header>

      <main className={`bs-tutorial-board ${isPractice ? "" : "no-dock"}`}>
        <section className="bs-tutorial-coach" aria-live="polite">
          <small>SPROUT SCHOOL · CHAPTER {chapter + 1} OF 5</small>
          <h1>{title}</h1>
          <p>{body}</p>
          {phase === "goal" && (
            <button className="bs-cta bs-tutorial-next" onClick={() => setPhase("pickPlant")}>Earn my first petal →</button>
          )}
          {phase === "endTurn" && (
            <div className="bs-tutorial-math"><span>3 ☀️ + 1</span><b>+</b><span>4 💧 − 1</span><b>=</b><strong>🌸</strong></div>
          )}
          {phase === "bloomComplete" && (
            <div className="bs-tutorial-finish-actions">
              <button className="bs-cta" onClick={() => setPhase("cardTypes")}>Meet the cards →</button>
              <button className="bs-link" onClick={reset}>Practice again</button>
            </div>
          )}
          {phase === "cardTypes" && (
            <button className="bs-cta bs-tutorial-next" disabled={seenCardTypes.length < 3} onClick={() => setPhase("biomes")}>
              {seenCardTypes.length < 3 ? `Inspect all 3 (${seenCardTypes.length}/3)` : "Explore the biomes →"}
            </button>
          )}
          {phase === "biomes" && (
            <button className="bs-cta bs-tutorial-next" onClick={() => setPhase("turns")}>See how turns flow →</button>
          )}
          {phase === "turns" && (
            <div className="bs-tutorial-finish-actions">
              {turnBeat < turnBeats.length - 1
                ? <button className="bs-cta" onClick={() => setTurnBeat((beat) => beat + 1)}>Next beat →</button>
                : <button className="bs-cta" onClick={onBack}>Choose my team</button>}
              <button className="bs-link" onClick={() => setTurnBeat(0)}>Restart round</button>
            </div>
          )}
        </section>

        <section className={`bs-tutorial-garden ${phase === "bloomComplete" ? "bloomed" : ""}`} aria-label="Tutorial example">
          {phase === "goal" ? (
            <div className="bs-tutorial-goal">
              <span className="bs-tutorial-goal-flower"><PetalFlower petals={PETALS_TO_WIN} size={148} /></span>
              <strong>First to {PETALS_TO_WIN} blooms wins</strong>
              <div><span>YOU <b>0 / {PETALS_TO_WIN}</b></span><i>VS</i><span>SPROUT <b>0 / {PETALS_TO_WIN}</b></span></div>
              <small>☀️ target + 💧 sweet spot = 🌸 one petal</small>
            </div>
          ) : phase === "bloomComplete" ? (
            <div className="bs-tutorial-bloom">
              <div className="bs-tutorial-flower"><PetalFlower petals={1} size={72} /></div>
              <span className="bs-tutorial-grown"><LeaflingSprite speciesId="pilea" plant={plant} stage="grown" /></span>
              <strong>1 / {PETALS_TO_WIN} petals!</strong>
            </div>
          ) : phase === "cardTypes" ? (
            <div className="bs-tutorial-card-lesson">
              <div className="bs-tutorial-card-deck">
                {TUTORIAL_TYPE_CARDS.map((card) => (
                  <TrayCard key={card.uid} card={card} selected={card.id === cardFocus} onClick={() => inspectType(card)} stats />
                ))}
              </div>
              <div className={`bs-tutorial-card-copy type-${focusedCard.type}`}>
                <span>{focusedCard.icon} {focusedCard.type}</span>
                <strong>{focusedCard.name}</strong>
                <p>{focusedCard.text}</p>
              </div>
            </div>
          ) : phase === "biomes" ? (
            <div className="bs-tutorial-biomes">
              <div className="bs-tutorial-biome-tabs">
                {BIOME_IDS.map((id) => (
                  <button key={id} className={id === biomeFocus ? "on" : ""} onClick={() => setBiomeFocus(id)}>
                    <i>{BIOMES[id].icon}</i><span>{BIOMES[id].name}</span>
                  </button>
                ))}
              </div>
              <div className="bs-tutorial-biome-card">
                <small>FORECAST DIE</small><h2>{focusedBiome.icon} {focusedBiome.name}</h2><p>{focusedBiome.blurb}</p>
                <OddsDie odds={focusedBiome.odds} />
                <div className="bs-tutorial-weather-key">
                  {Object.values(WEATHER).map((weather) => (
                    <span key={weather.id}><b>{weather.icon} {focusedBiome.odds[weather.id]}/6</b><small>{weather.text}</small></span>
                  ))}
                </div>
                <em>The NEXT chip shows the exact forecast coming after your turn.</em>
              </div>
            </div>
          ) : phase === "turns" ? (
            <div className="bs-tutorial-turns">
              {turnBeats.map((beat, i) => (
                <button key={beat.title} className={`${i < turnBeat ? "done" : ""} ${i === turnBeat ? "on" : ""}`} onClick={() => setTurnBeat(i)}>
                  <i>{i < turnBeat ? "✓" : beat.icon}</i><span><strong>{i + 1}. {beat.title}</strong><small>{beat.text}</small></span>
                </button>
              ))}
              <p>Then repeat: draw → act → weather → rival, until someone banks {PETALS_TO_WIN} blooms.</p>
            </div>
          ) : (
            <PlantActor
              plant={plant}
              slotClass="tutorial-slot"
              isTarget={phase === "placePlant" || plantIsTarget}
              onClick={handleGarden}
            />
          )}
        </section>
      </main>

      {phase === "forecast" && (
        <div className="bs-weather-banner bs-tutorial-weather" role="status">
          <span className="bs-weather-ico">⛅</span>
          <div><strong>Cloudy</strong><p>+1 ☀️ · −1 💧</p></div>
        </div>
      )}

      {isPractice && <footer className="bs-tutorial-dock">
        <div className="bs-tutorial-actions">
          <button
            className={`bs-menu-btn ${phase === "waterPlant" ? "on" : ""}`}
            disabled={phase !== "chooseWater" && phase !== "waterPlant"}
            onClick={() => phase === "chooseWater" && setPhase("waterPlant")}
          >💧 Water {watered ? "✓" : ""}</button>
          <button className={`bs-menu-btn ${plant ? "used" : ""}`} disabled>🌱 Plant {plant ? "✓" : ""}</button>
          <button
            className="bs-menu-btn end"
            disabled={phase !== "endTurn"}
            onClick={() => setPhase("forecast")}
          >End turn ▶</button>
        </div>
        <div className="bs-tutorial-hand" aria-label="Your hand">
          {!plant && (
            <TrayCard
              card={TUTORIAL_CARDS[0]}
              selected={pileaSelected}
              disabled={phase !== "pickPlant" && phase !== "placePlant"}
              unplayable={phase !== "pickPlant" && phase !== "placePlant"}
              onClick={handlePlantCard}
              stats
            />
          )}
          {!treated && (
            <TrayCard
              card={TUTORIAL_CARDS[1]}
              selected={fertilizerSelected}
              disabled={phase !== "pickTreatment" && phase !== "applyTreatment"}
              unplayable={phase !== "pickTreatment" && phase !== "applyTreatment"}
              onClick={handleFertilizer}
            />
          )}
        </div>
      </footer>}
    </div>
  );
}

/* -------------------------------------------------- how-to-play guide */
// Illustrated with the game's own graphics: live sprites, a real gauge
// plate, real card frames — so the guide always matches the game.

const GUIDE_PLANT = {
  uid: "guide", speciesId: "pilea", light: 3, water: 3,
  umbrella: false, growLamp: true, humidifier: false,
};
const GUIDE_CARDS = [
  { uid: "gc1", id: "calathea" },
  { uid: "gc2", id: "fertilizer" },
  { uid: "gc3", id: "rootRot" },
];

function GuideScreen({ onBack }) {
  return (
    <div className="bs-stage bs-title">
      <BiomeBackdrop biomeId="mosswood" />
      <div className="bs-title-veil" />
      <div className="bs-title-panel bs-guide">
        <button className="bs-link" onClick={onBack}>← back to title</button>
        <h1>How to play</h1>
        <p className="bs-title-tag">Grow {PETALS_TO_WIN} Leaflings before your rival. That's the whole war.</p>

        <div className="bs-guide-grid">
          <section className="bs-guide-card">
            <h3>🌸 The goal</h3>
            <div className="bs-guide-center"><PetalFlower petals={4} size={84} /></div>
            <p>Every Leafling that <strong>blooms</strong> adds a petal to your flower. {PETALS_TO_WIN} petals and you win — blooms are banked forever, safe from every trick.</p>
          </section>

          <section className="bs-guide-card">
            <h3>🪴 Reading your Leafling</h3>
            <div className="bs-guide-demo">
              <span className="bs-guide-sprite"><LeaflingSprite speciesId="pilea" plant={GUIDE_PLANT} /></span>
              <GaugePlate plant={GUIDE_PLANT} />
            </div>
            <ul className="bs-guide-list">
              <li><strong>☀️ bar</strong> fills toward its 🎯 light target — it turns green when the target is hit.</li>
              <li><strong>💧 cells</strong>: the green-ringed cells are the <em>sweet spot</em>. Blue = in it, orange = too dry or flooded.</li>
              <li>Hit the ☀️ 🎯 <em>while</em> 💧 sits in its 🎯 sweet spot → instant bloom. 🌸</li>
              <li>Little icons: 🔦 grow lamp · 🫧 humidifier · ☂️ blighted!</li>
            </ul>
          </section>

          <section className="bs-guide-card">
            <h3>🎴 On your turn</h3>
            <ul className="bs-guide-list">
              <li>You draw a card automatically.</li>
              <li>Once: <strong>🌱 plant</strong> a Leafling <em>or</em> <strong>🪏 dig one up</strong> (its card shuffles back into your deck — escape hatch for blighted or flooded pots).</li>
              <li>Once: <strong>💧 water</strong> one of your plants.</li>
              <li>Treatments &amp; blights: <strong>as many as you like</strong>.</li>
              <li><strong>End turn ▶</strong> — and the weather hits <em>everyone</em>.</li>
            </ul>
          </section>

          <section className="bs-guide-card">
            <h3>⛅ The sky decides</h3>
            <div className="bs-sky-pop-row"><b>☀️ Sunny</b><span>{WEATHER.sunny.text}</span></div>
            <div className="bs-sky-pop-row"><b>⛅ Cloudy</b><span>{WEATHER.cloudy.text}</span></div>
            <div className="bs-sky-pop-row"><b>🌧️ Rain</b><span>{WEATHER.rain.text}</span></div>
            <p>The <strong>NEXT</strong> chip always shows what's coming — plan around it! Each biome loads the dice differently (tap the biome chip in-game to see its odds).</p>
          </section>

          <section className="bs-guide-card wide">
            <h3>🃏 The cards</h3>
            <div className="bs-guide-cards-row">
              {GUIDE_CARDS.map((c) => (
                <TrayCard key={c.uid} card={c} onClick={() => {}} />
              ))}
            </div>
            <p><span className="bs-swatch plant" /> <strong>Plants</strong> go in pots — or compost onto a matching plant for +1 ☀️ +1 💧. <span className="bs-swatch treatment" /> <strong>Treatments</strong> boost your garden. <span className="bs-swatch blight" /> <strong>Blights</strong> hit your rival's. Hover or tap any card in your tray to see exactly what it does.</p>
          </section>

          <section className="bs-guide-card">
            <h3>🐛 Dirty tricks (and cures)</h3>
            <ul className="bs-guide-list">
              <li><strong>☂️ Umbrella</strong> cuts a plant off from all weather. Cure: Repot it, or dig it up.</li>
              <li><strong>🦠 Root Rot</strong> floods a plant with +5 💧. Cure: drain with Irrigation or Repot's fresh soil.</li>
              <li><strong>🪓 Shovel</strong> destroys a rival seedling (under 4 ☀️ <em>and</em> 4 💧) — grow fast!</li>
              <li><strong>🐛 Pests</strong> reset a plant's ☀️ and 💧 to zero. Brutal on a near-bloomer.</li>
            </ul>
          </section>

          <section className="bs-guide-card">
            <h3>⚔ Playing a friend</h3>
            <ol className="bs-guide-list">
              <li>You both open this same address — no accounts, you play as guests.</li>
              <li>One of you hits <strong>⚔ Play a friend → Create match</strong> and gets a 4-letter code.</li>
              <li>The other hits <strong>Join match</strong> and types the code. That's it!</li>
            </ol>
            <div className="bs-guide-code">CODE&nbsp;→&nbsp;<b>JUFC</b></div>
            <p className="bs-guide-tip">💡 Easy species (more 🌿 = harder) make great closers — save them for the final petals.</p>
          </section>

          <section className="bs-guide-card wide">
            <h3>📇 Every card</h3>
            <div className="bs-codex">
              {["plant", "treatment", "blight"].map((type) => (
                <div key={type} className="bs-codex-group">
                  <h4>{type === "plant" ? "🌱 Plants — hit ☀️ target while 💧 is in range to bloom" : type === "treatment" ? "🧪 Treatments — help your own garden" : "🐛 Blights — sabotage your rival's"}</h4>
                  <div className="bs-codex-list">
                    {Object.values(CARDS).filter((c) => c.type === type).map((c) => {
                      const sp = type === "plant" ? SPECIES[c.id] : null;
                      return (
                        <div key={c.id} className={`bs-codex-card type-${type}`}>
                          <span className="bs-codex-ico">
                            {type === "plant" ? <LeaflingSprite speciesId={c.id} /> : <i>{c.icon}</i>}
                          </span>
                          <div className="bs-codex-body">
                            <strong>
                              {c.name}
                              {sp && <span className="bs-codex-req"> 🎯 ☀️{sp.lightNeed} · 💧{sp.waterMin === sp.waterMax ? sp.waterMin : `${sp.waterMin}–${sp.waterMax}`}</span>}
                            </strong>
                            <p>{sp ? sp.blurb : c.text}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="bs-title-actions">
          <button className="bs-cta" onClick={onBack}>🌱 Got it — let's play</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- lobby screen */

function LobbyScreen({ picked, onBack, onHosted, onJoined }) {
  const [name, setName] = useState(() => localStorage.getItem("bbGardenerName") || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(null); // 'create' | 'join'
  const [error, setError] = useState(null);

  const remember = (n) => localStorage.setItem("bbGardenerName", n);

  const create = async () => {
    setBusy("create"); setError(null);
    try {
      const user = await online.ensureGuest();
      const n = name.trim() || "Gardener";
      remember(n);
      const { matchId, joinCode } = await online.createMatch(n, picked);
      onHosted({ matchId, joinCode, uid: user.uid, myName: n });
    } catch (e) {
      setError(e.message || "Could not create the match.");
      setBusy(null);
    }
  };

  const join = async () => {
    setBusy("join"); setError(null);
    try {
      const user = await online.ensureGuest();
      const n = name.trim() || "Gardener";
      remember(n);
      const { matchId } = await online.joinMatch(code, n, picked);
      onJoined({ matchId, uid: user.uid, myName: n });
    } catch (e) {
      setError(e.message || "Could not join that match.");
      setBusy(null);
    }
  };

  return (
    <div className="bs-stage bs-title">
      <BiomeBackdrop biomeId="glasshouse" />
      <div className="bs-title-veil" />
      <div className="bs-title-panel">
        <button className="bs-link" onClick={onBack}>← back</button>
        <h1>Play a friend</h1>
        <p className="bs-title-tag">You both play as guests — nothing to install, nothing to sign up for.</p>
        <div className="bs-lobby">
          <label className="bs-field">
            <span>Your gardener name</span>
            <input
              value={name}
              maxLength={16}
              placeholder="e.g. Fern Gully"
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <div className="bs-lobby-cols">
            <div className="bs-lobby-card">
              <strong>Host a match</strong>
              <p>You'll get a 4-letter code to send to your friend.</p>
              <button className="bs-cta" disabled={!!busy} onClick={create}>
                {busy === "create" ? "Creating…" : "Create match"}
              </button>
            </div>
            <div className="bs-lobby-card">
              <strong>Join a match</strong>
              <p>Type the code your friend sent you.</p>
              <input
                className="bs-code-input"
                value={code}
                maxLength={4}
                placeholder="CODE"
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              />
              <button className="bs-cta" disabled={!!busy || code.length !== 4} onClick={join}>
                {busy === "join" ? "Joining…" : "Join match"}
              </button>
            </div>
          </div>
          <p className="bs-draft-note">Your team: {picked.map((id) => SPECIES[id].name).join(", ")} (change it on the title screen)</p>
          {error && <p className="bs-lobby-error">⚠️ {error}</p>}
        </div>
      </div>
    </div>
  );
}

function WaitingScreen({ joinCode, onCancel }) {
  return (
    <div className="bs-stage">
      <BiomeBackdrop biomeId="glasshouse" />
      <div className="bs-roll-plate landed">
        <small>MATCH CODE</small>
        <h2 className="bs-join-code">{joinCode}</h2>
        <p>Send this code to your friend — they hit "Play a friend → Join match" at the same address.</p>
        <p className="bs-deal">Waiting for them to join…</p>
        <button className="bs-link" onClick={onCancel}>cancel match</button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- roll screen */

function RollScreen({ finalId, onDone }) {
  const [shownId, setShownId] = useState(BIOME_IDS[0]);
  const [landed, setLanded] = useState(false);
  useEffect(() => {
    let i = 0;
    const spin = setInterval(() => { i += 1; setShownId(BIOME_IDS[i % BIOME_IDS.length]); }, 340);
    const stop = setTimeout(() => { clearInterval(spin); setShownId(finalId); setLanded(true); }, 2100);
    const done = setTimeout(onDone, 3600);
    return () => { clearInterval(spin); clearTimeout(stop); clearTimeout(done); };
  }, [finalId, onDone]);
  const biome = BIOMES[shownId];
  return (
    <div className="bs-stage">
      <BiomeBackdrop biomeId={shownId} />
      <div className={`bs-roll-plate ${landed ? "landed" : ""}`}>
        <small>ROLLING THE BIOME</small>
        <h2>{biome.icon} {biome.name}</h2>
        <p>{biome.blurb}</p>
        <div className="bs-odds"><OddsDie odds={biome.odds} /><span>the daily forecast die</span></div>
        {landed && <p className="bs-deal">Dealing opening hands…</p>}
      </div>
    </div>
  );
}

/* ---------------------------------------------- battle FX (state diffs) */

const collectFx = (prev, next) => {
  const out = [];
  // When the forecast resolves it changes every plant at once — the weather
  // banner already summarizes that, so skip the noisy per-plant delta pills.
  const weatherResolved = next.lastWeather?.key !== prev.lastWeather?.key;
  for (let p = 0; p < 2; p++) {
    const before = prev.players[p];
    const after = next.players[p];
    before.garden.forEach((pl, slot) => {
      if (!pl) return;
      const slotClass = `bs-slot-${p === AI ? "e" : "p"}${slot}`;
      const nx = after.garden[slot];
      if (!nx || nx.uid !== pl.uid) {
        const kind = after.petals > before.petals ? "bloom" : "gone";
        out.push({ slotClass, enemy: p === AI, kind, speciesId: pl.speciesId, phase: 0 });
      } else {
        const items = [];
        const dl = nx.light - pl.light;
        const dw = nx.water - pl.water;
        if (dl) items.push(`${dl > 0 ? "+" : "−"}${Math.abs(dl)} ☀️`);
        if (dw) items.push(`${dw > 0 ? "+" : "−"}${Math.abs(dw)} 💧`);
        if (!pl.umbrella && nx.umbrella) items.push("☂️ blighted!");
        if (pl.umbrella && !nx.umbrella) items.push("☂️ cleared");
        if (!pl.growLamp && nx.growLamp) items.push("🔦 lamp on");
        if (!pl.humidifier && nx.humidifier) items.push("🫧 misting");
        if (items.length && !weatherResolved) out.push({ slotClass, enemy: p === AI, kind: "delta", items });
      }
    });
    after.garden.forEach((nx, slot) => {
      if (nx && !before.garden.some((pl) => pl && pl.uid === nx.uid)) {
        out.push({ slotClass: `bs-slot-${p === AI ? "e" : "p"}${slot}`, enemy: p === AI, kind: "sprout" });
      }
    });
  }
  // Stagger simultaneous blooms so they play out one after another.
  out.filter((f) => f.kind === "bloom").forEach((f, i) => { f.phase = i; });
  return out;
};

const runEngine = (state, idx, action) => {
  switch (action.kind) {
    case "plant": return plantCard(state, idx, action.handUid, action.slot);
    case "compost": return compostCard(state, idx, action.handUid, action.plantUid);
    case "water": return waterPlant(state, idx, action.plantUid);
    case "digUp": return digUpPlant(state, idx, action.plantUid);
    case "treatment": return playTreatment(state, idx, action.handUid, action.payload || {});
    case "blight": return playBlight(state, idx, action.handUid, action.payload || {});
    case "end": return endTurn(state);
    default: return state;
  }
};

/* ================================================================ page */

export default function BattleStage() {
  const [screen, setScreen] = useState(() =>
    window.location.hash.toLowerCase() === "#howtoplay" ? "guide" : "title"
  ); // title | guide | tutorial | lobby | waiting | roll | battle
  const [game, setGame] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [rollInfo, setRollInfo] = useState(null);
  const [match, setMatch] = useState(null); // { matchId, uid, myName, joinCode? } when online
  const [netBusy, setNetBusy] = useState(false);

  const [selectedUid, setSelectedUid] = useState(null);
  const [hoverUid, setHoverUid] = useState(null);
  const [mode, setMode] = useState(null); // 'water' | 'dig'
  const [step, setStep] = useState(null);
  const [picker, setPicker] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aiSpeed, setAiSpeed] = useState(() => localStorage.getItem("bbAiSpeed") || "normal");
  const [reveal, setReveal] = useState(null); // { cardId, subtitle, manual, owner, key }
  const [logCard, setLogCard] = useState(null); // cardId tapped in the log
  const [handInspect, setHandInspect] = useState(null); // own card tapped while waiting
  const [inspectPlant, setInspectPlant] = useState(null); // { plant, owner } tapped in play
  const [intro, setIntro] = useState(null); // pre-game team walkthrough: { list, i }
  const introPendingRef = useRef(null); // species to walk through once an online board lands
  // Online lag: deterministic actions apply locally the instant you tap, then
  // confirm against the server snapshot. These refs keep that honest.
  const serverViewRef = useRef(null); // last server-confirmed view
  const sendChainRef = useRef(Promise.resolve()); // actions commit in tap order
  const pendingSendsRef = useRef(0); // optimistic actions not yet server-confirmed
  const [skyInfo, setSkyInfo] = useState(null);
  const [notice, setNotice] = useState("Pick a card from your tray, or use a turn action.");
  const [weatherShow, setWeatherShow] = useState(null);
  const [fxList, setFxList] = useState([]);
  const [callout, setCallout] = useState(null);
  const fxSeq = useRef(0);
  const aiSteps = useRef(0);
  const prevGameRef = useRef(null);
  const [titlePicked, setTitlePicked] = useState(["calathea", "snake", "zz"]);

  useEffect(() => {
    const openLinkedGuide = () => {
      if (window.location.hash.toLowerCase() === "#howtoplay") setScreen("guide");
    };
    window.addEventListener("hashchange", openLinkedGuide);
    return () => window.removeEventListener("hashchange", openLinkedGuide);
  }, []);

  const isOnline = !!match;
  const meP = game?.players[HUMAN];
  const oppP = game?.players[AI];
  const oppName = oppP?.name || "Sprout";
  const myTurn =
    game && game.phase === "play" && game.active === HUMAN && !weatherShow && !netBusy;
  // whose pill to highlight — tracks the engine's active side during play
  const turnSide = game && game.phase === "play" ? game.active : null;
  const selected = meP?.hand.find((c) => c.uid === selectedUid);
  const validPlantUids = useMemo(
    () => new Set(selected ? getTargets(game, HUMAN, selected.id).map((p) => p.uid) : []),
    [game, selected]
  );

  const clearSelection = () => { setSelectedUid(null); setMode(null); setStep(null); setPicker(null); };
  // Hover-to-preview for the biome/forecast chips — only on pointer devices,
  // so taps on touch use the click toggle cleanly (no double-tap).
  const hoverInfo = (key) =>
    CAN_HOVER
      ? { onMouseEnter: () => setSkyInfo(key), onMouseLeave: () => setSkyInfo((s) => (s === key ? null : s)) }
      : {};

  const applyFx = (prev, next) => {
    if (!prev || !next) return;
    const fx = collectFx(prev, next).map((f) => ({ ...f, id: ++fxSeq.current }));
    if (!fx.length) return;
    setFxList((cur) => [...cur, ...fx]);
    const ids = new Set(fx.map((f) => f.id));
    // Blooms hold longer so the evolve flourish reads as a beat; staggered
    // simultaneous blooms need extra time for the later phases to finish.
    const bloomCount = fx.filter((f) => f.kind === "bloom").length;
    const life = bloomCount ? 1750 + 900 * (bloomCount - 1) : 1500;
    setTimeout(() => setFxList((cur) => cur.filter((f) => !ids.has(f.id))), life);
  };

  const showCallout = (icon, text) => {
    const key = ++fxSeq.current;
    setCallout({ icon, text, key });
    setTimeout(() => setCallout((c) => (c?.key === key ? null : c)), 1900);
  };

  const updateGame = (next) => {
    prevGameRef.current = game;
    setGame(next);
  };

  /* ---- solo match setup ---- */
  const startMatch = (playerSpecies) => {
    setRollInfo({ biomeId: rollBiome(), playerSpecies });
    setDemoMode(false);
    setMatch(null);
    setScreen("roll");
  };
  const beginRolledMatch = () => {
    const pool = [...SPECIES_IDS].sort(() => Math.random() - 0.5);
    const aiSpecies = pool.slice(0, 2 + Math.floor(Math.random() * 2));
    updateGame(newGame({ playerSpecies: rollInfo.playerSpecies, aiSpecies, biomeId: rollInfo.biomeId }));
    clearSelection();
    aiSteps.current = 0;
    setNotice("Your move! Plant a Leafling and watch the sky.");
    setIntro({ list: rollInfo.playerSpecies, i: 0 }); // meet your team before turn one
    setScreen("battle");
  };
  const loadDemo = () => {
    updateGame(createDemoGame());
    setDemoMode(true);
    setMatch(null);
    clearSelection();
    aiSteps.current = 0;
    setNotice(`Demo garden, turn four — race Sprout to ${PETALS_TO_WIN} blooms!`);
    setScreen("battle");
  };
  const backToTitle = () => {
    setGame(null);
    prevGameRef.current = null;
    setMatch(null);
    clearSelection();
    setScreen("title");
  };
  const openGuide = () => {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#howtoplay`);
    setScreen("guide");
  };
  const closeGuide = () => {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    setScreen("title");
  };

  /* ---- online match lifecycle ---- */
  useEffect(() => {
    if (!match?.matchId) return undefined;
    const unsub = online.subscribeMatch(
      match.matchId,
      match.uid,
      (update) => {
        if (update.status === "waiting") {
          setScreen("waiting");
          return;
        }
        setGame((prev) => {
          const next = update.view;
          serverViewRef.current = next;
          // Running ahead of the server (optimistic taps in flight): ignore
          // snapshots that trail the local log — the commit snapshot converges.
          if (prev && pendingSendsRef.current > 0) {
            const lastId = (s) => (s.log.length ? s.log[s.log.length - 1].id : -1);
            if (lastId(next) < lastId(prev)) return prev;
          }
          if (prev) {
            applyFx(prev, next);
            // Reveal / call out the opponent's plays as they land.
            const prevMax = prev.log.length ? prev.log[prev.log.length - 1].id : -1;
            const freshOpp = next.log.filter((e) => e.id > prevMax && e.actor !== match.myName);
            const cardPlay = freshOpp.find((e) => e.card);
            if (cardPlay) {
              const key = ++fxSeq.current;
              setReveal({ cardId: cardPlay.card, subtitle: `${next.players[1].name} played`, manual: false, owner: "sprout", key });
              setTimeout(() => setReveal((r) => (r?.key === key ? null : r)), 1400);
            } else {
              const act = freshOpp.find((e) => e.kind === "action" || e.kind === "blight");
              if (act) showCallout(act.icon, act.text);
            }
          }
          prevGameRef.current = prev;
          return next;
        });
        setNetBusy(false);
        if (introPendingRef.current) { // board just landed — walk the team first
          setIntro({ list: introPendingRef.current, i: 0 });
          introPendingRef.current = null;
        }
        setScreen((s) => (s === "battle" ? s : "battle"));
      },
      (err) => setNotice(`Connection hiccup: ${err.message}`)
    );
    return unsub;
  }, [match?.matchId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- weather + AI pacing ---- */
  useEffect(() => {
    if (!game?.lastWeather) return undefined;
    setWeatherShow(game.lastWeather.id);
    const t = setTimeout(() => setWeatherShow(null), 2300);
    return () => clearTimeout(t);
  }, [game?.lastWeather?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOnline || screen !== "battle" || !game || game.phase !== "play" || game.active !== AI || weatherShow || reveal)
      return undefined;
    const speed = SPEED[aiSpeed] || SPEED.normal;
    const timer = setTimeout(() => {
      aiSteps.current += 1;
      const action = aiSteps.current > AI_STEP_CAP ? { kind: "end" } : chooseAiAction(game);
      let next = runEngine(game, AI, action);
      if (action.kind === "end" || next === game) {
        next = endTurn(game);
        aiSteps.current = 0;
      }
      if (next !== game) {
        applyFx(game, next);
        const fresh = next.log.slice(game.log.length);
        const cardEntry = fresh.find((e) => e.card);
        if (cardEntry) {
          // Big front-and-center reveal of the card Sprout just played.
          const key = ++fxSeq.current;
          const manual = aiSpeed === "manual";
          setReveal({ cardId: cardEntry.card, subtitle: "Sprout played", manual, owner: "sprout", key });
          if (!manual) setTimeout(() => setReveal((r) => (r?.key === key ? null : r)), speed.reveal);
        } else {
          const act = fresh.find((e) => e.kind === "action" || e.kind === "blight");
          if (act) showCallout(act.icon, act.text);
        }
      }
      updateGame(next);
    }, speed.step);
    return () => clearTimeout(timer);
  }, [game, screen, weatherShow, isOnline, reveal, aiSpeed]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- one dispatcher for both modes ---- */
  const perform = (action, msg) => {
    if (!isOnline) {
      const next = runEngine(game, HUMAN, action);
      if (next === game) return false;
      applyFx(game, next);
      updateGame(next);
      clearSelection();
      if (msg) setNotice(msg);
      return true;
    }
    if (netBusy) return false;
    // Send in tap order; on failure, fall back to the last server truth (later
    // optimistic taps were built on the failed one, so they roll back too).
    const trackedSend = () => {
      pendingSendsRef.current += 1;
      sendChainRef.current = sendChainRef.current
        .then(() => online.sendAction(match.matchId, action))
        .then(() => { pendingSendsRef.current -= 1; })
        .catch((e) => {
          pendingSendsRef.current -= 1;
          setNotice(`⚠️ ${e.message || "That move didn't go through."}`);
          setNetBusy(false);
          if (serverViewRef.current) { prevGameRef.current = null; setGame(serverViewRef.current); }
        });
    };
    // Deterministic moves apply the moment you tap — the shared engine plays
    // them locally and the server snapshot confirms. Only server-side RNG
    // (end-turn weather + draws, Shopping, Planter's deck pull) waits.
    const handCard = action.handUid ? meP.hand.find((c) => c.uid === action.handUid) : null;
    const serverOnly = action.kind === "end" || (handCard && ["shopping", "planter"].includes(handCard.id));
    if (!serverOnly) {
      const next = runEngine(game, HUMAN, action);
      if (next === game) return false; // engine rejected — nothing to send
      applyFx(game, next);
      updateGame(next);
      clearSelection();
      if (msg) setNotice(msg);
      trackedSend();
      return true;
    }
    setNetBusy(true);
    clearSelection();
    if (msg) setNotice(msg);
    trackedSend();
    return true;
  };

  /* ---- hand interaction ---- */
  const canPlay = (card) => {
    if (!game) return false;
    const def = CARDS[card.id];
    const mine = meP.garden.filter(Boolean);
    if (def.type === "plant")
      return ((!meP.usedPlantAction && meP.garden.some((s) => s === null)) ||
        getTargets(game, HUMAN, card.id).length > 0);
    if (def.type === "blight") return getTargets(game, HUMAN, card.id).length > 0;
    switch (card.id) {
      case "shopping": return true;
      case "recycle": return meP.discard.some((c) => CARDS[c.id].type !== "blight");
      case "planter":
        return !meP.usedPlantAction && meP.garden.some((s) => s === null) &&
          meP.deck.some((c) => CARDS[c.id].type === "plant");
      case "irrigation":
      case "greenhouse": return mine.length > 0;
      default: return getTargets(game, HUMAN, card.id).length > 0;
    }
  };

  const selectCard = (card) => {
    if (!myTurn) { setHandInspect(card.id); return; }
    setHandInspect(null);
    setMode(null); setStep(null); setPicker(null);
    if (selectedUid === card.uid) { setSelectedUid(null); return; }
    if (!canPlay(card)) { setNotice(`${CARDS[card.id].name} has no valid play right now.`); setSelectedUid(null); return; }
    const def = CARDS[card.id];
    if (card.id === "shopping") {
      setSelectedUid(card.uid);
      setStep({ kind: "shopping" });
      setNotice("Shopping Trip: confirm to draw 2 cards.");
      return;
    }
    setSelectedUid(card.uid);
    if (card.id === "recycle") { setPicker("recycle"); return; }
    if (card.id === "planter") { setPicker("planter"); return; }
    // No preselected doses/dials — the player must choose before targets open up.
    if (card.id === "fertilizer") { setStep({ kind: "fertilizer", water: null }); setNotice("Fertilizer: choose the dose, then tap your Leafling."); }
    else if (card.id === "rootRot") { setNotice("Root Rot: tap a rival to add 5 💧."); }
    else if (card.id === "irrigation") { setStep({ kind: "irrigation", dir: null, amount: null }); setNotice("Irrigation: choose pour or drain and how much, then tap ONE of your Leaflings."); }
    else if (card.id === "greenhouse") { setStep({ kind: "greenhouse", ops: [] }); setNotice("Greenhouse: choose an adjustment over up to 3 Leaflings, then confirm."); }
    else if (card.id === "repot") { setNotice("Repot: tap a Leafling with 3+ ☀️ — then pick its bonus."); }
    else if (def.type === "plant") { setNotice("Tap an empty spot to plant — or a matching Leafling to compost."); }
    else { setNotice("Bouncing arrows mark valid targets."); }
  };

  const actOnPlant = (plant, owner, slot) => {
    if (!myTurn) return;
    if (mode === "water" && owner === HUMAN && plant)
      return perform({ kind: "water", plantUid: plant.uid }, "Glug glug. 💧 spent for this turn.");
    if (mode === "dig" && owner === HUMAN && plant)
      return perform({ kind: "digUp", plantUid: plant.uid }, "Dug up — the card is shuffled back into your deck.");
    if (!selected) return;
    const def = CARDS[selected.id];

    if (def.type === "plant") {
      if (!plant && owner === HUMAN)
        return perform({ kind: "plant", handUid: selected.uid, slot }, "Planted! The forecast does the rest.");
      if (plant && owner === HUMAN && plant.speciesId === selected.id)
        return perform({ kind: "compost", handUid: selected.uid, plantUid: plant.uid }, "Composted: +1 ☀️ +1 💧.");
      return;
    }
    if (def.type === "blight" && plant && owner === AI) {
      const payload = { plantUid: plant.uid };
      if (selected.id === "rootRot") payload.amount = 5;
      return perform({ kind: "blight", handUid: selected.uid, payload }, "Blight delivered. 😈");
    }
    if (def.type === "treatment" && plant && owner === HUMAN) {
      if (step?.kind === "irrigation") {
        if (step.dir == null || step.amount == null) return; // must pick both first
        return perform({
          kind: "treatment",
          handUid: selected.uid,
          payload: { plantUid: plant.uid, amount: step.dir * step.amount },
        }, step.dir > 0 ? `Irrigation poured +${step.amount} 💧.` : `Irrigation drained −${step.amount} 💧.`);
      }
      if (selected.id === "repot") { setStep({ kind: "repotBonus", plantUid: plant.uid }); setNotice("Repot: choose the finishing touch."); return; }
      if (selected.id === "fertilizer") {
        if (step?.water == null) return; // must pick the dose first
        return perform({ kind: "treatment", handUid: selected.uid, payload: { plantUid: plant.uid, water: step.water } }, "Fertilized.");
      }
      return perform({ kind: "treatment", handUid: selected.uid, payload: { plantUid: plant.uid } }, "Treatment applied.");
    }
  };

  const isTarget = (plant, owner) => {
    if (!myTurn) return false;
    if (mode === "water") return owner === HUMAN && Boolean(plant);
    if (mode === "dig") return owner === HUMAN && Boolean(plant);
    if (!selected || step?.kind === "repotBonus" || picker) return false;
    const def = CARDS[selected.id];
    if (def.type === "plant")
      return owner === HUMAN && ((!plant && !meP.usedPlantAction) || plant?.speciesId === selected.id);
    // dose/dial cards: targets stay closed until the player has made their choice
    if (step?.kind === "irrigation") return owner === HUMAN && Boolean(plant) && step.dir != null && step.amount != null;
    if (step?.kind === "greenhouse") return false; // per-plant controls handle Greenhouse
    if (step?.kind === "fertilizer" && step.water == null) return false;
    return Boolean(plant && validPlantUids.has(plant.uid));
  };

  const commitPartial = () => {
    if (step?.kind === "greenhouse" && step.ops.length > 0) {
      perform({ kind: "treatment", handUid: selectedUid, payload: { ops: step.ops } }, "Greenhouse adjusted.");
    }
  };

  const queueGreenhouse = (plantUid, stat, delta) => {
    if (!myTurn || step?.kind !== "greenhouse") return;
    const existing = step.ops.findIndex((op) => op.plantUid === plantUid);
    const op = { plantUid, stat, delta };
    const ops = existing === -1
      ? step.ops.length < 3 ? [...step.ops, op] : step.ops
      : step.ops.map((queued, index) => index === existing ? op : queued);
    setStep({ ...step, ops });
    setNotice(`Greenhouse: ${ops.length} of 3 Leaflings queued. Confirm when ready.`);
  };

  const finishTurn = () => {
    if (!myTurn) return;
    aiSteps.current = 0;
    perform({ kind: "end" });
  };

  /* ---- text box chips ---- */
  const stepChips = () => {
    if (!myTurn || !step) return null;
    if (step.kind === "fertilizer")
      return (<>
        <button className={`bs-chip ${step.water === 1 ? "on" : ""}`} onClick={() => setStep({ ...step, water: 1 })}>+1 ☀️ &amp; +1 💧</button>
        <button className={`bs-chip ${step.water === 0 ? "on" : ""}`} onClick={() => setStep({ ...step, water: 0 })}>+1 ☀️ only</button>
      </>);
    if (step.kind === "shopping")
      return <button className="bs-chip go" onClick={() => perform({ kind: "treatment", handUid: selectedUid, payload: {} }, "Shopping Trip! Two cards added to your tray.")}>Confirm: draw 2 ✓</button>;
    if (step.kind === "irrigation")
      return (<>
        <button className={`bs-chip ${step.dir === 1 ? "on" : ""}`} onClick={() => setStep({ ...step, dir: 1 })}>pour +💧</button>
        <button className={`bs-chip ${step.dir === -1 ? "on" : ""}`} onClick={() => setStep({ ...step, dir: -1 })}>drain −💧</button>
        {[1, 2, 3].map((n) => (
          <button key={n} className={`bs-chip ${step.amount === n ? "on" : ""}`} disabled={step.dir == null}
            onClick={() => setStep({ ...step, amount: n })}>{step.dir == null ? "" : step.dir > 0 ? "+" : "−"}{n}💧</button>
        ))}
      </>);
    if (step.kind === "greenhouse")
      return (<>
        <button className="bs-chip go" disabled={step.ops.length === 0} onClick={commitPartial}>Confirm Greenhouse ✓ ({step.ops.length})</button>
      </>);
    if (step.kind === "repotBonus")
      return (<>
        <button className="bs-chip go" onClick={() => perform({ kind: "treatment", handUid: selectedUid, payload: { plantUid: step.plantUid, bonus: "light" } }, "Repotted: +1 ☀️.")}>+1 ☀️</button>
        <button className="bs-chip go" onClick={() => perform({ kind: "treatment", handUid: selectedUid, payload: { plantUid: step.plantUid, bonus: "water" } }, "Repotted: +1 💧.")}>+1 💧</button>
        <button className="bs-chip go" onClick={() => perform({ kind: "treatment", handUid: selectedUid, payload: { plantUid: step.plantUid, bonus: "drain" } }, "Fresh dry soil: −3 💧.")}>fresh soil −3 💧</button>
      </>);
    return null;
  };

  /* ================================================================ render */

  if (screen === "title")
    return (
      <TitleScreen
        onStart={(picked) => { setTitlePicked(picked); startMatch(picked); }}
        onDemo={loadDemo}
        onFriend={(picked) => { setTitlePicked(picked); setScreen("lobby"); }}
        onGuide={openGuide}
        onTutorial={() => setScreen("tutorial")}
      />
    );
  if (screen === "tutorial") return <TutorialScreen onBack={() => setScreen("title")} />;
  if (screen === "guide") return <GuideScreen onBack={closeGuide} />;
  if (screen === "lobby")
    return (
      <LobbyScreen
        picked={titlePicked}
        onBack={backToTitle}
        onHosted={({ matchId, joinCode, uid, myName }) => { introPendingRef.current = titlePicked; setMatch({ matchId, uid, myName, joinCode }); setDemoMode(false); setScreen("waiting"); }}
        onJoined={({ matchId, uid, myName }) => { introPendingRef.current = titlePicked; setMatch({ matchId, uid, myName }); setDemoMode(false); setNotice("Match joined! Waiting for the board…"); }}
      />
    );
  if (screen === "waiting")
    return <WaitingScreen joinCode={match?.joinCode || "…"} onCancel={backToTitle} />;
  if (screen === "roll") return <RollScreen finalId={rollInfo.biomeId} onDone={beginRolledMatch} />;
  if (!game) return null;

  const biome = BIOMES[game.biomeId];
  const forecast = WEATHER[game.nextForecast];
  const lastLog = game.log[game.log.length - 1];
  const previewCard = (!picker && (meP.hand.find((c) => c.uid === hoverUid) || selected)) || null;
  const waitingPreview = handInspect
    ? meP.hand.find((card) => card.id === handInspect) || null
    : null;
  const textBoxLine = myTurn
    ? notice
    : game.phase !== "play"
      ? "The match is over."
      : netBusy
        ? "Sending your move…"
        : lastLog
          ? lastLog.text
          : `${oppName} is thinking…`;

  return (
    <div className={`bs-stage ${weatherShow ? `bs-weather weather-${weatherShow}` : ""}`}>
      <BiomeBackdrop biomeId={game.biomeId} />

      <div className="bs-fx bs-fx-sun" />
      <div className="bs-fx bs-fx-cloud"><i /><i /></div>
      <div className="bs-fx bs-fx-rain" />

      {/* sky chrome */}
      <header className="bs-sky">
        <div className={`bs-trainer enemy ${turnSide === AI ? "active" : ""}`}>
          <span className="bs-avatar">{isOnline ? "🧑‍🌾" : "🧚"}</span>
          <div>
            <strong>{oppName}</strong>
            <small>🂠 {oppP.hand.length} · 🎴 {oppP.deck.length}{game.active === AI && game.phase === "play" ? (isOnline ? " · their turn" : " · thinking…") : ""}</small>
          </div>
          <PetalFlower petals={oppP.petals} size={38} />
        </div>
        <div className="bs-sky-right">
          <button
            className="bs-sky-chip btn"
            onClick={() => setSkyInfo(skyInfo === "biome" ? null : "biome")}
            {...hoverInfo("biome")}
          >{biome.icon} {biome.name}</button>
          <button
            className="bs-sky-chip forecast btn"
            onClick={() => setSkyInfo(skyInfo === "forecast" ? null : "forecast")}
            {...hoverInfo("forecast")}
          >NEXT&nbsp;{forecast.icon} {forecast.name}</button>
          <button className="bs-sky-chip btn" onClick={() => setLogOpen(!logOpen)}>📜 Log</button>
          <button className="bs-sky-chip btn" onClick={() => setSettingsOpen(true)}>⚙</button>
          {skyInfo === "biome" && (
            <div className="bs-sky-pop">
              <strong>{biome.icon} {biome.name}</strong>
              <p>{biome.blurb}</p>
              <small>THE FORECAST DIE HERE</small>
              {["sunny", "cloudy", "rain"].map((w) => (
                <div key={w} className="bs-sky-pop-row">
                  <b>{WEATHER[w].icon} {WEATHER[w].name} <OddsRow weatherId={w} count={biome.odds[w]} /></b>
                  <span>{WEATHER[w].text}</span>
                </div>
              ))}
            </div>
          )}
          {skyInfo === "forecast" && (
            <div className="bs-sky-pop">
              <strong>{forecast.icon} Next forecast: {forecast.name}</strong>
              <p>{forecast.text}</p>
              <small>Resolves for every plant on the table — Umbrella blocks it, a Humidifier keeps 💧 steady, and Grow Lamps add +1 ☀️ — the moment either gardener ends their turn. Plan around it!</small>
            </div>
          )}
        </div>
      </header>

      {/* rows */}
      {oppP.garden.map((plant, slot) => (
        <PlantActor
          key={plant?.uid || `e${slot}`}
          plant={plant}
          slotClass={`bs-slot-e${slot}`}
          enemy
          isTarget={isTarget(plant, AI)}
          onClick={() => actOnPlant(plant, AI, slot)}
          onInspect={plant ? () => setInspectPlant({ plant, owner: "sprout" }) : undefined}
        />
      ))}
      {meP.garden.map((plant, slot) => {
        const ghOp = step?.kind === "greenhouse" && plant
          ? step.ops.find((o) => o.plantUid === plant.uid)
          : null;
        return (
          <PlantActor
            key={plant?.uid || `p${slot}`}
            plant={plant}
            slotClass={`bs-slot-p${slot}`}
            enemy={false}
            isTarget={isTarget(plant, HUMAN)}
            onClick={() => actOnPlant(plant, HUMAN, slot)}
            onInspect={plant ? () => setInspectPlant({ plant, owner: "you" }) : undefined}
            greenhouseOp={step?.kind === "greenhouse" && plant
              ? step.ops.find((op) => op.plantUid === plant.uid)
              : null}
            greenhouseFull={step?.kind === "greenhouse" && step.ops.length >= 3}
            onGreenhouseChange={step?.kind === "greenhouse" && plant
              ? (stat, delta) => queueGreenhouse(plant.uid, stat, delta)
              : null}
            badge={
              ghOp
                ? `${ghOp.delta > 0 ? "+" : "−"}1 ${ghOp.stat === "light" ? "☀️" : "💧"} queued`
                : mode === "dig" && plant
                  ? "tap to dig up"
                  : null
            }
          />
        );
      })}

      {/* battle FX */}
      {fxList.map((f) => (
        <div
          key={f.id}
          className={`bs-fxpop ${f.slotClass} ${f.enemy ? "enemy" : ""} kind-${f.kind}`}
          style={f.phase ? { "--phase-delay": `${f.phase * 0.9}s` } : undefined}
        >
          {f.kind === "delta" &&
            f.items.map((it, i) => (
              <span key={i} className={`bs-delta ${it.startsWith("−") ? "neg" : "pos"}`} style={{ animationDelay: `${i * 0.15}s` }}>
                {it}
              </span>
            ))}
          {f.kind === "bloom" && (
            <>
              <span className="bs-evolve">
                <LeaflingSprite speciesId={f.speciesId} owner={f.enemy ? "sprout" : "you"} stage="grown" crown />
              </span>
              <span className="bs-bloomburst">
                {Array.from({ length: 8 }, (_, i) => (
                  <i key={i} style={{ "--ang": `${i * 45}deg` }}>❀</i>
                ))}
              </span>
              <span className="bs-bloom-label">🌸 Bloomed!</span>
            </>
          )}
          {f.kind === "sprout" && <span className="bs-sproutpop">🌱</span>}
          {f.kind === "gone" && <span className="bs-gonepuff">💨</span>}
        </div>
      ))}

      {callout && (
        <div key={callout.key} className="bs-callout">
          <span>{callout.icon}</span>
          <p>{callout.text}</p>
        </div>
      )}

      {weatherShow && (
        <div className="bs-weather-banner">
          <span className="bs-weather-ico">{WEATHER[weatherShow].icon}</span>
          <div>
            <strong>{WEATHER[weatherShow].name}!</strong>
            <p>{WEATHER[weatherShow].text}</p>
          </div>
        </div>
      )}

      {(waitingPreview || previewCard) && (
        <CardInfo
          card={waitingPreview || previewCard}
          pinned={Boolean(waitingPreview) || previewCard === selected}
          onCancel={waitingPreview ? () => setHandInspect(null) : clearSelection}
        />
      )}

      {/* dock */}
      <footer className="bs-dock">
        <div className="bs-dock-top">
          <div className={`bs-textbox ${myTurn ? "" : "waiting"}`}>
            <p>{textBoxLine}</p>
            {myTurn && lastLog && <small className="bs-ticker">{lastLog.icon} {lastLog.text}</small>}
            {(step || mode) && myTurn && (
              <div className="bs-chiprow">
                {stepChips()}
                <button className="bs-chip cancel" onClick={clearSelection}>cancel</button>
              </div>
            )}
          </div>
          <div className="bs-menu">
            <button
              className={`bs-menu-btn ${mode === "water" ? "on" : ""}`}
              disabled={!myTurn || meP.usedWater || !meP.garden.some(Boolean)}
              onClick={() => { setMode(mode === "water" ? null : "water"); setSelectedUid(null); setStep(null); }}
            >💧 Water {meP.usedWater ? "✓" : ""}</button>
            <button
              className={`bs-menu-btn ${mode === "dig" ? "on" : ""}`}
              disabled={!myTurn || meP.usedPlantAction || !meP.garden.some(Boolean)}
              onClick={() => { setMode(mode === "dig" ? null : "dig"); setSelectedUid(null); setStep(null); }}
            >🪏 Dig up {meP.usedPlantAction ? "✓" : ""}</button>
            <span className={`bs-menu-note ${meP.usedPlantAction ? "used" : ""}`} title="Once per turn: plant a Leafling OR dig one up (Planter counts too)">🌱 Plant {meP.usedPlantAction ? "✓" : ""}</span>
            <button className="bs-menu-btn end" disabled={!myTurn} onClick={finishTurn}>End turn ▶</button>
          </div>
          <div className={`bs-trainer you ${turnSide === HUMAN ? "active" : ""}`}>
            <span className="bs-avatar">🧑‍🌾</span>
            <div>
              <strong>{isOnline ? match.myName : "You"}</strong>
              <small>🂠 {meP.hand.length} · 🎴 {meP.deck.length} · 🗑 {meP.discard.length}</small>
            </div>
            <PetalFlower petals={meP.petals} size={44} />
          </div>
        </div>
        <div className="bs-tray-wrap">
          <div className="bs-tray">
            {meP.hand.length === 0 && <span className="bs-tray-empty">Tray empty — end the turn to draw.</span>}
            {meP.hand.map((card) => (
              <TrayCard
                key={card.uid}
                card={card}
                selected={card.uid === selectedUid}
                unplayable={myTurn && !canPlay(card)}
                onClick={() => selectCard(card)}
                onHover={setHoverUid}
              />
            ))}
          </div>
        </div>
      </footer>

      {/* pickers */}
      {picker && (
        <div className="bs-overlay" onClick={clearSelection}>
          <div className="bs-picker" onClick={(e) => e.stopPropagation()}>
            <h2>{picker === "recycle" ? "♻️ Recycle" : "🏺 Planter"}</h2>
            <p>{picker === "recycle"
              ? "Take a treatment or plant from your discard pile back into your tray."
              : "Plant any plant card straight from your deck (uses your plant action)."}</p>
            <div className="bs-picker-cards">
              {(picker === "recycle"
                ? meP.discard.filter((c) => CARDS[c.id].type !== "blight")
                : // Planter: one card per distinct species still in the deck
                  [...new Map(
                    meP.deck.filter((c) => CARDS[c.id].type === "plant").map((c) => [c.id, c])
                  ).values()]
              ).map((c) => (
                <TrayCard key={c.uid} card={c} stats={picker === "planter"} onClick={() => {
                  if (picker === "recycle")
                    perform({ kind: "treatment", handUid: selectedUid, payload: { discardUid: c.uid } }, `${CARDS[c.id].name} back in your tray.`);
                  else {
                    const slotIdx = meP.garden.findIndex((s) => s === null);
                    perform({ kind: "treatment", handUid: selectedUid, payload: { deckUid: c.uid, slotIdx } }, `${CARDS[c.id].name} planted straight from the deck.`);
                  }
                }} />
              ))}
            </div>
            <button className="bs-link" onClick={clearSelection}>never mind</button>
          </div>
        </div>
      )}

      {/* Sprout / opponent card reveal, front and center */}
      {reveal && (
        <CardModal
          cardId={reveal.cardId}
          subtitle={reveal.subtitle}
          owner={reveal.owner}
          interactive={reveal.manual}
          onDismiss={reveal.manual ? () => setReveal(null) : undefined}
          dismissLabel="Next ▶"
        />
      )}

      {/* tapped a log entry's card */}
      {logCard && (
        <CardModal cardId={logCard} subtitle="Card played" owner="you" onDismiss={() => setLogCard(null)} />
      )}

      {/* tapped a plant's info panel in play */}
      {inspectPlant && (
        <PlantModal plant={inspectPlant.plant} owner={inspectPlant.owner} onDismiss={() => setInspectPlant(null)} />
      )}

      {/* pre-game walkthrough: tap through your team, then start */}
      {intro && (
        <CardModal
          cardId={intro.list[intro.i]}
          subtitle={`Meet your team · ${intro.i + 1} of ${intro.list.length}`}
          owner="you"
          onDismiss={() => setIntro(intro.i + 1 < intro.list.length ? { ...intro, i: intro.i + 1 } : null)}
          dismissLabel={intro.i + 1 < intro.list.length ? "Next ▶" : "Let's grow! ▶"}
        />
      )}

      {/* settings menu (holds New/Leave + Sprout pace) */}
      {settingsOpen && (
        <div className="bs-overlay" onClick={() => setSettingsOpen(false)}>
          <div className="bs-picker settings" onClick={(e) => e.stopPropagation()}>
            <h2>⚙ Settings</h2>
            {!isOnline && (
              <div className="bs-set-row">
                <span>Sprout's pace</span>
                <div className="bs-seg">
                  {[["manual", "Manual"], ["slow", "Slow"], ["normal", "Normal"], ["fast", "Fast"]].map(([v, label]) => (
                    <button
                      key={v}
                      className={aiSpeed === v ? "on" : ""}
                      onClick={() => { setAiSpeed(v); localStorage.setItem("bbAiSpeed", v); }}
                    >{label}</button>
                  ))}
                </div>
              </div>
            )}
            <p className="bs-set-hint">
              {isOnline
                ? "Playing a friend — each move appears as they make it."
                : "Manual pauses on each card Sprout plays; tap Next to advance."}
            </p>
            <div className="bs-confirm-actions">
              <button
                className="bs-cta"
                onClick={() => { setSettingsOpen(false); if (game.phase === "play") setConfirmLeave(true); else backToTitle(); }}
              >{isOnline ? "🚪 Leave match" : "🌱 New game"}</button>
              {demoMode && <button className="bs-ghost" onClick={() => { setSettingsOpen(false); loadDemo(); }}>↻ Reset demo</button>}
              <button className="bs-ghost" onClick={() => setSettingsOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* confirm leaving an in-progress match */}
      {confirmLeave && (
        <div className="bs-overlay" onClick={() => setConfirmLeave(false)}>
          <div className="bs-picker confirm" onClick={(e) => e.stopPropagation()}>
            <h2>{isOnline ? "Leave this match?" : "Start a new game?"}</h2>
            <p>
              {isOnline
                ? `You'll forfeit your match against ${oppName}.`
                : "Your current game against Sprout will be abandoned."}
            </p>
            <div className="bs-confirm-actions">
              <button className="bs-cta" onClick={() => { setConfirmLeave(false); backToTitle(); }}>
                {isOnline ? "Leave match" : "New game"}
              </button>
              <button className="bs-ghost" onClick={() => setConfirmLeave(false)}>Keep playing</button>
            </div>
          </div>
        </div>
      )}

      {/* log drawer */}
      <aside className={`bs-log ${logOpen ? "open" : ""}`}>
        <div className="bs-log-head">
          <div><small>{biome.name.toUpperCase()} · TURN {game.turn}</small><h2>Garden log</h2></div>
          <button onClick={() => setLogOpen(false)}>×</button>
        </div>
        <div className="bs-log-list">
          {[...game.log].reverse().map((e) => (
            <article
              key={e.id}
              className={`kind-${e.kind} ${e.card ? "tappable" : ""}`}
              onClick={e.card ? () => setLogCard(e.card) : undefined}
            >
              {e.card ? <LogCardThumb cardId={e.card} /> : <span>{e.icon}</span>}
              <div><small>TURN {e.turn}{e.card ? " · tap card" : ""}</small><p>{e.text}</p></div>
            </article>
          ))}
        </div>
      </aside>

      {/* victory */}
      {game.phase === "over" && !weatherShow && (
        <div className="bs-overlay victory">
          <div className="bs-confetti">{Array.from({ length: 14 }, (_, i) => <i key={i}>❀</i>)}</div>
          <div className="bs-victory-plate">
            <PetalFlower petals={PETALS_TO_WIN} size={104} />
            <h2>{game.winner === HUMAN ? "Full bloom — you win!" : `${oppName} wins!`}</h2>
            <p>
              {game.winner === HUMAN
                ? `${PETALS_TO_WIN} Leaflings grown under the ${biome.name} sky.`
                : "Out-gardened this time. Shuffle up and take revenge."}
            </p>
            <div className="bs-victory-actions">
              <button className="bs-cta" onClick={backToTitle}>🌱 {isOnline ? "Back to title" : "New match"}</button>
              <button className="bs-ghost" onClick={() => setLogOpen(true)}>📜 View the garden log</button>
              {demoMode && <button className="bs-ghost" onClick={loadDemo}>↻ Replay demo</button>}
              <button className="bs-ghost" onClick={() => openInNewTab(APP_STORE_URL)}>Get Leaflings for iOS</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
